import { FiDataSourceBase, FiDataSourceSettings, ILoadDataArgs } from './fi-data-source-base.directive';
import { ChangeDetectorRef, Directive, Injector, OnDestroy } from '@angular/core';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatSortDirection, ObjectTypes } from '@fi-shared/types';
import { UserPreferencesService } from '@fi-shared/fi-shared-services/user-preferences.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { isDefined } from '@fi-shared/utils';
import { groupBy } from '@fi-shared/utils/group-by';

export type TSortOrder<T> = (item: T) => number;

@Directive({
    standalone: false,
})
export abstract class FiInfiniteScrollDataSource<T> extends FiDataSourceBase<T> implements OnDestroy {
    selectedSort: MatSort = new MatSort();
    private initialData: T[];
    protected skipInitSelectedSort: boolean = false;
    protected skipGetSortDataFromLocalStorage: boolean = true;
    protected defaultSortColumnName: string;
    protected defaultSortDirection: boolean = false;
    abstract itemsListType: FiItemsListType;
    abstract objectType: ObjectTypes;
    protected userPreferencesService: UserPreferencesService;
    protected cdr: ChangeDetectorRef;

    protected get sortColumn(): string {
        return this.selectedSort.active;
    }
    protected get sortDirection(): boolean {
        return this.selectedSort.direction === 'desc';
    }

    protected getSortOrderFn(): TSortOrder<T> {
        // override if custom sort needed
        let isStringField: boolean = !!this.items().find((item) => typeof item[this.selectedSort.active] === typeof '');

        return (item: T) => {
            const prop = item[this.selectedSort.active];
            if (Array.isArray(prop)) return prop.length;
            if (!isDefined(prop)) {
                return isStringField ? '' : Number.MIN_SAFE_INTEGER;
            }
            return typeof prop === typeof '' ? prop.toLowerCase() : prop;
        };
    }

    protected constructor(injector: Injector, settings?: FiDataSourceSettings) {
        super(injector, settings);
        this.userPreferencesService = injector.get(UserPreferencesService);
        this.cdr = injector.get(ChangeDetectorRef);
    }

    /*ngOnInit() {
        //this.getListData();
        this.initSortData();
        //this.reloadData();
    }*/

    protected reloadData(loadArgs?: ILoadDataArgs) {
        if (!loadArgs?.fetchOnlyItems) {
            this.initialData = undefined;
        }
        return super.reloadData(loadArgs);
    }

    abstract getListData(): Promise<T[]>;
    protected initSelectedSort() {
        let newSelectedSort: MatSort;
        if (this.skipInitSelectedSort) {
            newSelectedSort = new MatSort();
        } else if (this.skipGetSortDataFromLocalStorage) {
            newSelectedSort = this.getDefaultSortData();
        } else {
            newSelectedSort = this.getSortDataFromLocalStorage() || this.getDefaultSortData();
        }
        if (!isDefined(this.selectedSort.direction)) {
            newSelectedSort.direction = MatSortDirection.Ascending;
        }

        this.selectedSort = newSelectedSort;
    }

    protected getSortDataFromLocalStorage(): MatSort {
        return this.userPreferencesService.getListSort(this.itemsListType);
    }

    private getDefaultSortData(): MatSort {
        const sort: MatSort = this.getSortData(this.defaultSortColumnName, this.defaultSortDirection);
        return sort;
    }

    protected getSortData(sortColumnName: string, sortDirection: boolean): MatSort {
        const sort: MatSort = new MatSort();
        sort.active = sortColumnName;
        sort.direction = this.getMatSortDirection(sortDirection);
        return sort;
    }

    onSortChanged($event: Sort) {
        this.updateCurrentSort($event);
        this.sortData();
        this.reloadData({ fetchOnlyItems: true });
    }

    protected updateCurrentSort(newSort: Sort) {
        this.selectedSort.active = newSort.active;
        this.selectedSort.direction = newSort.direction;
        if (!this.skipGetSortDataFromLocalStorage) {
            this.userPreferencesService.saveListSort(this.itemsListType, this.selectedSort);
        }
    }

    getMatSortDirection(sortDirection: boolean): SortDirection {
        return sortDirection ? 'desc' : 'asc';
    }

    protected isGroupSorting: boolean;
    protected groupIdProp: string;
    protected groupSortColumnName: string;

    private sortData() {
        if (!this.initialData?.length) return;

        const sortOrderFn: TSortOrder<T> = this.getSortOrderFn();
        const comparatorFn: (a: T, b: T) => number = comparator((item) => sortOrderFn(item), this.isDescendingSort());

        if (this.isGroupSorting && this.groupSortColumnName !== this.selectedSort.active) {
            const itemGroup: { [key: string]: T[] } = groupBy(this.initialData, this.groupIdProp);
            const sortedMergedArray: T[] = Object.values(itemGroup)
                .map((group: T[]) => group.sort(comparatorFn))
                .flat();
            this.initialData = [...sortedMergedArray];
            return;
        }

        this.initialData.sort(comparatorFn);
    }

    protected isDescendingSort(): boolean {
        return this.selectedSort.direction === MatSortDirection.Descending;
    }

    private firstLoading = true;
    loadItemsFn(loadArgs: ILoadDataArgs): Promise<T[]> {
        return new Promise<T[]>((resolve) => {
            const resolveFn = () => {
                // https://chainformation.atlassian.net/browse/FI-6398
                // in case of group sorting we should return full initial data
                const items = !this.isGroupSorting
                    ? this.initialData.slice(
                          loadArgs.fetchParams.offset,
                          loadArgs.fetchParams.offset + loadArgs.fetchParams.numberOfRecordsToTake,
                      )
                    : this.initialData;
                resolve(items);
            };

            // for proper loading animation we should load initial data here
            if (!this.initialData) {
                this.getListData().then((initialData) => {
                    this.initialData = initialData;

                    if (this.firstLoading) {
                        this.initSelectedSort();
                    }
                    if (this.selectedSort.active) {
                        this.sortData();
                    }

                    this.setTotalCount(initialData.length);

                    this.firstLoading = false;
                    resolveFn();
                });
            } else {
                resolveFn();
            }
        });
    }

    onScrollToBottom() {
        return this.loadNext();
    }
}
